import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@cargomatic/ui';
import { Info, Pencil } from 'lucide-react';
import { withTranslation } from 'react-i18next';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import { showToast } from 'stores/ToastStore';

export const ROUTERS = {
  GOOGLE_MILES: { name: 'Google Miles', value: 'Google Miles' },
  PC_MILER: { name: 'PC Miler', value: 'PC Miler' },
  RAND_MC_NALLY: { name: 'Rand McNally', value: 'Rand McNally' },
};

export const DISTANCE_METHODS = {
  COORDINATES: { name: 'Latitude/Longitude', value: 'coordinates' },
  ZIP_TO_ZIP: { name: 'ZIP Code to ZIP Code', value: 'zip_to_zip' },
};

const ROUTE_TYPES = [
  ROUTERS.GOOGLE_MILES,
  ROUTERS.PC_MILER,
  ROUTERS.RAND_MC_NALLY,
];

const DISTANCE_METHOD_OPTIONS = [
  DISTANCE_METHODS.COORDINATES,
  DISTANCE_METHODS.ZIP_TO_ZIP,
];

const [oldestVersion, latestVersion] = [18, 36];

const PC_MILER_VERSIONS = [...Array(latestVersion - oldestVersion + 1)].map(
  (_, index) => ({
    name: `${ROUTERS.PC_MILER.value} ${index + oldestVersion}`,
    value: `${ROUTERS.PC_MILER.value} ${index + oldestVersion}`,
  }),
);

const ROUTING_CONFIGURATIONS = [
  ...PC_MILER_VERSIONS,
  { name: 'Current', value: 'Current' },
];

const EMPTY_VALUE_DISPLAY = '—';

function FieldLabel({ htmlFor, label, tooltip }) {
  return (
    <div className="flex items-center gap-1 mb-1">
      <Label htmlFor={htmlFor}>{label}</Label>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center"
              aria-label={tooltip}
            >
              <Info
                className="h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
            </button>
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function getDistanceMethodLabel(value) {
  const match = DISTANCE_METHOD_OPTIONS.find((item) => item.value === value);
  return match ? match.name : value;
}

function RoutingConfigurationComponent(props) {
  const { t, shipper } = props;
  const [routingType, setRoutingType] = useState('');
  const [routingConfig, setRoutingConfig] = useState('');
  const [distanceMethod, setDistanceMethod] = useState('');
  const [savedValues, setSavedValues] = useState({
    routingType: '',
    routingConfig: '',
    distanceMethod: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function load() {
      if (!shipper?._id) {
        setRoutingType('');
        setRoutingConfig('');
        setDistanceMethod('');
        setSavedValues({
          routingType: '',
          routingConfig: '',
          distanceMethod: '',
        });
        return;
      }
      try {
        const response = await UserWebAPIUtils.retrieveRoutingConfiguration(
          shipper._id,
        );
        const config = response?.routingConfiguration;
        const loaded = {
          routingType: config?.routing_type ?? '',
          routingConfig: config?.routing_configuration ?? '',
          distanceMethod: config?.distance_method ?? '',
        };
        setRoutingType(loaded.routingType);
        setRoutingConfig(loaded.routingConfig);
        setDistanceMethod(loaded.distanceMethod);
        setSavedValues(loaded);
      } catch (err) {
        console.log('Problem getting routing config ', err);
      }
    }
    load();
  }, [shipper?._id]);

  const handleRoutingTypeChange = (value) => {
    setRoutingType(value);
    if (value !== ROUTERS.PC_MILER.value) {
      setRoutingConfig('');
      setDistanceMethod('');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setRoutingType(savedValues.routingType);
    setRoutingConfig(savedValues.routingConfig);
    setDistanceMethod(savedValues.distanceMethod);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!shipper?._id) return;
    if (!routingType) return;
    if (
      routingType === ROUTERS.PC_MILER.value &&
      (!routingConfig || !distanceMethod)
    ) {
      return;
    }

    const isPcMilerSave = routingType === ROUTERS.PC_MILER.value;
    const payload = isPcMilerSave
      ? {
          routing_type: routingType,
          routing_configuration: routingConfig,
          distance_method: distanceMethod,
        }
      : { routing_type: routingType, routing_configuration: 'Current' };

    setIsSaving(true);
    try {
      const response = await UserWebAPIUtils.updateRoutingConfiguration(
        shipper._id,
        { routingConfiguration: payload },
      );
      if (response.success === true) {
        setSavedValues({
          routingType,
          routingConfig: isPcMilerSave ? routingConfig : '',
          distanceMethod: isPcMilerSave ? distanceMethod : '',
        });
        setIsEditing(false);
        showToast('Success', t('ROUTING_CONFIG_UPDATE_SUCCESS'), 'success');
      } else {
        showToast('Error', t('ROUTING_CONFIG_UPDATE_FAILURE'), 'error');
      }
    } catch (err) {
      console.log('Problem updating routing config ', err);
      showToast(
        'Error',
        t('ROUTING_CONFIG_UPDATE_FAILURE_ERR', { err }),
        'error',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const isPcMiler = routingType === ROUTERS.PC_MILER.value;
  const isSavedPcMiler = savedValues.routingType === ROUTERS.PC_MILER.value;
  const saveDisabled =
    isSaving ||
    !routingType ||
    (isPcMiler && (!routingConfig || !distanceMethod));

  const renderHeader = () => (
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>{t('ROUTING_CONFIGURATION')}</CardTitle>
      {!isEditing && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleEdit}
          aria-label={t('EDIT')}
          title={t('EDIT')}
          data-testid="routing-config-edit-button"
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}
    </CardHeader>
  );

  if (!isEditing) {
    return (
      <Card>
        {renderHeader()}
        <CardContent>
          <div className="flex flex-wrap gap-6">
            <div className="w-48">
              <FieldLabel
                htmlFor="routing-type-display"
                label={t('ROUTING_TYPE')}
                tooltip={t('ROUTING_TYPE_TIP')}
              />
              <div id="routing-type-display" className="text-sm">
                {savedValues.routingType || EMPTY_VALUE_DISPLAY}
              </div>
            </div>
            <div className="w-48">
              <FieldLabel
                htmlFor="routing-configuration-display"
                label={t('ROUTING_CONFIGURATION')}
                tooltip={t('ROUTING_CONFIGURATION_TIP')}
              />
              <div id="routing-configuration-display" className="text-sm">
                {savedValues.routingConfig || 'Current'}
              </div>
            </div>
            <div className="w-48">
              <FieldLabel
                htmlFor="distance-method-display"
                label="Distance Method"
                tooltip="Please select a distance method for distance calculations."
              />
              <div id="distance-method-display" className="text-sm">
                {isSavedPcMiler && savedValues.distanceMethod
                  ? getDistanceMethodLabel(savedValues.distanceMethod)
                  : EMPTY_VALUE_DISPLAY}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {renderHeader()}
      <CardContent>
        <div className="flex flex-wrap gap-6">
          <div className="w-48">
            <FieldLabel
              htmlFor="routing-type"
              label={t('ROUTING_TYPE')}
              tooltip={t('ROUTING_TYPE_TIP')}
            />
            <Select value={routingType} onValueChange={handleRoutingTypeChange}>
              <SelectTrigger
                id="routing-type"
                className="w-full [&>span]:flex-1 [&>span]:text-left"
              >
                <SelectValue placeholder={t('SELECT_ROUTING_TYPE')} />
              </SelectTrigger>
              <SelectContent>
                {ROUTE_TYPES.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isPcMiler && (
            <div className="w-48">
              <FieldLabel
                htmlFor="routing-configuration"
                label={t('ROUTING_CONFIGURATION')}
                tooltip={t('ROUTING_CONFIGURATION_TIP')}
              />
              <Select value={routingConfig} onValueChange={setRoutingConfig}>
                <SelectTrigger
                  id="routing-configuration"
                  className="w-full [&>span]:flex-1 [&>span]:text-left"
                >
                  <SelectValue placeholder={t('SELECT_ROUTING_CONFIG')} />
                </SelectTrigger>
                <SelectContent>
                  {ROUTING_CONFIGURATIONS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isPcMiler && (
            <div className="w-48">
              <FieldLabel
                htmlFor="distance-method"
                label="Distance Method"
                tooltip="Please select a distance method for distance calculations."
              />
              <Select value={distanceMethod} onValueChange={setDistanceMethod}>
                <SelectTrigger
                  id="distance-method"
                  className="w-full [&>span]:flex-1 [&>span]:text-left"
                >
                  <SelectValue placeholder="Select distance method" />
                </SelectTrigger>
                <SelectContent>
                  {DISTANCE_METHOD_OPTIONS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="mt-4 flex gap-2">
          <Button type="button" onClick={handleSave} disabled={saveDisabled}>
            {t('SAVE')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            {t('CANCEL')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export const RoutingConfiguration = withTranslation()(
  RoutingConfigurationComponent,
);
